import React, { ReactNode } from 'react';
import styled from 'styled-components';

interface TooltipPosition {
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  transform?: string;
}

const getTooltipPosition = (position: 'top' | 'bottom' | 'left' | 'right'): TooltipPosition => {
  switch (position) {
    case 'top':
      return {
        bottom: 'calc(100% + 8px)',
        left: '50%',
        transform: 'translateX(-50%)',
      };
    case 'bottom':
      return {
        top: 'calc(100% + 8px)',
        left: '50%',
        transform: 'translateX(-50%)',
      };
    case 'left':
      return {
        right: 'calc(100% + 8px)',
        top: '50%',
        transform: 'translateY(-50%)',
      };
    case 'right':
    default:
      return {
        left: 'calc(100% + 8px)',
        top: '50%',
        transform: 'translateY(-50%)',
      };
  }
};

const TooltipContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const TooltipText = styled.div<{ position: 'top' | 'bottom' | 'left' | 'right' }>`
  visibility: hidden;
  opacity: 0;
  background-color: #333;
  color: #fff;
  text-align: center;
  border-radius: 4px;
  padding: 8px 12px;
  position: absolute;
  z-index: 10000;
  font-size: 12px;
  line-height: 1.4;
  white-space: normal;
  max-width: 250px;
  min-width: 100px;
  pointer-events: none;
  transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;
  font-family: 'Roboto', sans-serif;

  ${props => {
    const pos = getTooltipPosition(props.position);
    return `
      top: ${pos.top || 'auto'};
      bottom: ${pos.bottom || 'auto'};
      left: ${pos.left || 'auto'};
      right: ${pos.right || 'auto'};
      transform: ${pos.transform || 'none'};
    `;
  }}

  ${TooltipContainer}:hover & {
    visibility: visible;
    opacity: 1;
  }

  &::before {
    content: '';
    position: absolute;
    border-style: solid;
    border-width: 5px;
    
    ${props => {
      switch (props.position) {
        case 'top':
          return `
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            border-color: #333 transparent transparent transparent;
          `;
        case 'bottom':
          return `
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%);
            border-color: transparent transparent #333 transparent;
          `;
        case 'left':
          return `
            left: 100%;
            top: 50%;
            transform: translateY(-50%);
            border-color: transparent transparent transparent #333;
          `;
        case 'right':
        default:
          return `
            right: 100%;
            top: 50%;
            transform: translateY(-50%);
            border-color: transparent #333 transparent transparent;
          `;
      }
    }}
  }
`;

interface TooltipEnhancedProps {
  children: ReactNode;
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

const TooltipEnhanced: React.FC<TooltipEnhancedProps> = ({ 
  children, 
  content, 
  position = 'right' 
}) => {
  return (
    <TooltipContainer>
      {children}
      <TooltipText position={position}>{content}</TooltipText>
    </TooltipContainer>
  );
};

export default TooltipEnhanced;